import React, { useMemo, useRef, useEffect } from 'react';
import { AnalysisResult } from '../../contracts/scoring';
import { adaptToCleanlinessModule } from './cleanliness.adapter';
import { validateCleanliness } from './cleanliness.validate';
import { GhostNoteData } from '../../contracts/audio';
import { AdviceCard } from '../../components/SmartTrainer/AdviceCard';

interface CleanlinessTabProps {
  result: AnalysisResult;
}

type MetricCard = { label: string; value: string; hint?: string };

const fmt = (n: any, decimals = 0) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  return decimals > 0 ? x.toFixed(decimals) : String(Math.round(x));
};

const GhostAuditItem: React.FC<{ item: GhostNoteData; onSeek: (t: number) => void }> = ({ item, onSeek }) => {
  return (
    <div
      onClick={() => onSeek(item.time)}
      className="p-5 bg-white/[0.03] border border-white/5 rounded-[32px] flex items-center gap-6 group hover:bg-white/[0.08] hover:border-red-500/40 cursor-pointer transition-all duration-500"
    >
      <div className="w-12 h-12 rounded-2xl bg-black/40 flex items-center justify-center text-xl group-hover:bg-red-500 transition-colors shadow-lg">
        {item.type === 'RESONANCE' ? '🎸' : '👻'}
      </div>

      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-black text-white uppercase italic tracking-tighter">
            Event @ {item.time.toFixed(2)}s
          </span>
          <span
            className={`text-[8px] font-black px-2 py-0.5 rounded-full border ${
              item.type === 'RESONANCE'
                ? 'border-orange-500/40 text-orange-400'
                : 'border-red-500/40 text-red-400'
            }`}
          >
            {item.type}
          </span>
        </div>
        <p className="text-[11px] text-slate-400 italic">"Résonance parasite détectée hors de l'enveloppe d'attaque."</p>
      </div>

      <div className="text-right">
        <span className="text-[8px] font-black text-slate-600 uppercase tracking-widest block mb-1">Impact</span>
        <span className="text-xs font-mono font-bold text-red-400">-{Math.round((item.intensity || 0) * 100)} pts</span>
      </div>
    </div>
  );
};

export const CleanlinessTab: React.FC<CleanlinessTabProps> = ({ result }) => {
  const moduleResult = useMemo(() => {
    const adapted = adaptToCleanlinessModule(result);
    return validateCleanliness(adapted);
  }, [result]);

  const audioRef = useRef<HTMLAudioElement>(null);
  const seekTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (seekTimeoutRef.current) window.clearTimeout(seekTimeoutRef.current);
    };
  }, []);

  const diagnostics = result.feedback.diagnostics.filter(d => d.domain === 'CLEANLINESS');

  const stuUrl = result.alignedUrls?.stu || '';

  const handleSeek = (time: number) => {
    if (!audioRef.current || !stuUrl) return;

    try {
      audioRef.current.currentTime = Math.max(0, time - 0.5);
      void audioRef.current.play().catch(() => {});
      if (seekTimeoutRef.current) window.clearTimeout(seekTimeoutRef.current);
      seekTimeoutRef.current = window.setTimeout(() => {
        if (audioRef.current) audioRef.current.pause();
      }, 1200);
    } catch {
      // no-op
    }
  };

  // ✅ Si pas d'URL audio, on affiche un état propre plutôt que “non calculable”
  if (!stuUrl) {
    return (
      <div className="bg-[#02080a] p-12 rounded-[48px] border-2 border-dashed border-white/10 flex flex-col items-center justify-center min-h-[400px] text-center gap-6">
        <div className="text-5xl opacity-40">🎧</div>
        <div className="space-y-2">
          <h3 className="text-xl font-black text-white uppercase italic">Audio indisponible</h3>
          <p className="text-sm text-slate-500 italic max-w-xs leading-relaxed">
            Impossible de lire l'audio élève (alignedUrls.stu manquant).
            <br />
            <br />
            Les métriques peuvent exister, mais la timeline “forensic” ne peut pas isoler les événements sans audio.
          </p>
        </div>
      </div>
    );
  }

  if (moduleResult.status !== 'OK' || !moduleResult.data) {
    return <div className="p-20 text-center text-slate-500 italic">Analyse de propreté non calculable sur ce signal.</div>;
  }

  const { ghostNotes, ghostsPerMinute, thresholdErrPerMin } = moduleResult.data;
  const threshold = thresholdErrPerMin;
  const score = moduleResult.score || 0;

  // ✅ Bloc "Mesures détectées" (standard)
  const measuredCards: MetricCard[] = useMemo(() => {
    const durationSec = Number(result.session.duration || 0);
    const durationMin = Math.max(0.01, durationSec / 60);

    const totalEvents = ghostNotes?.length || 0;
    const resonances = ghostNotes?.filter(g => g.type === 'RESONANCE')?.length || 0;
    const ghosts = totalEvents - resonances;

    return [
      {
        label: 'Cleanliness score',
        value: `${fmt(score)}%`,
        hint: 'Plus haut = mieux',
      },
      {
        label: 'Errors per min',
        value: `${fmt(ghostsPerMinute, 1)} err/min`,
        hint: `Seuil: ${fmt(threshold, 1)} err/min (plus bas = mieux)`,
      },
      {
        label: 'Total events',
        value: String(totalEvents),
        hint: 'Résonances + ghosts détectés',
      },
      {
        label: 'Ghosts',
        value: String(ghosts),
        hint: 'Parasites hors intention',
      },
      {
        label: 'Resonances',
        value: String(resonances),
        hint: 'Résonances hors enveloppe',
      },
      {
        label: 'Duration',
        value: `${fmt(durationMin, 2)} min`,
        hint: 'Durée prise en compte',
      },
    ].slice(0, 6);
  }, [result.session.duration, ghostNotes, ghostsPerMinute, threshold, score]);

  // ✅ Barre: basée sur le threshold (au lieu de /50 hardcodé)
  //  - si ghostsPerMinute <= threshold => vert
  //  - sinon => rouge
  // largeur = ratio vs (threshold * 2) pour avoir une échelle lisible
  const barDenom = Math.max(1, threshold * 2);
  const barPct = Math.min(100, Math.max(0, (ghostsPerMinute / barDenom) * 100));
  const isOver = ghostsPerMinute > threshold;

  return (
    <div className="flex flex-col gap-10 animate-fade-in pb-12">
      <audio ref={audioRef} src={stuUrl} />

      {/* 1) SCORE + POURQUOI + CONSEILS */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4 bg-[#02080a] p-10 rounded-[48px] border border-white/10 shadow-2xl flex flex-col items-center text-center justify-center gap-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent"></div>
          <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.4em] italic">Muting Integrity</span>

          <div className="flex items-baseline gap-2">
            <span className={`text-8xl font-black italic tracking-tighter ${score > 85 ? 'text-emerald-400' : 'text-red-400'}`}>
              {score}
            </span>
            <span className="text-xl font-bold text-slate-700">/100</span>
          </div>

          <div className="w-full space-y-2 px-4">
            <div className="flex justify-between text-[8px] font-black text-slate-600 uppercase tracking-widest">
              <span>Bruit de fond</span>
              <span>{fmt(ghostsPerMinute, 1)} ERR/MIN</span>
            </div>
            <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-1000 ${isOver ? 'bg-red-500' : 'bg-emerald-500'}`}
                style={{ width: `${barPct}%` }}
              />
            </div>
            <div className="flex justify-between text-[8px] font-black text-slate-700 uppercase tracking-widest">
              <span>Seuil</span>
              <span>{fmt(threshold, 1)} ERR/MIN</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-8 bg-black/40 p-10 rounded-[48px] border border-white/10 flex flex-col justify-center gap-8 shadow-inner">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-red-500/20 flex items-center justify-center text-2xl">🔇</div>
            <h4 className="text-sm font-black text-white uppercase italic tracking-widest">Contrôle du Silence</h4>
          </div>

          <p className="text-lg text-slate-300 italic leading-relaxed pl-4 border-l-2 border-red-500/40">
            "La propreté est le premier critère de professionnalisme. L'IA traque les cordes à vide qui résonnent et les levées de doigts imprécises."
          </p>

          <div className="space-y-3">
            {diagnostics.length > 0 ? (
              diagnostics.map((adv, i) => <AdviceCard key={i} advice={adv} isHighContrast={false} />)
            ) : (
              <div className="p-6 bg-emerald-500/5 border border-emerald-500/20 rounded-3xl text-center">
                <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest flex items-center justify-center gap-3">
                  <span className="text-lg">✨</span> Zéro résonance parasite détectée
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ✅ 2) MESURES DÉTECTÉES (standard) */}
      <div className="bg-white/[0.02] border border-white/10 p-8 rounded-[48px] space-y-6">
        <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.3em]">Mesures détectées</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {measuredCards.map((m, i) => (
            <div key={i} className="bg-black/40 border border-white/10 rounded-2xl p-5">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{m.label}</div>
              <div className="text-2xl font-black italic text-white tabular-nums">{m.value}</div>
              {m.hint ? <div className="text-[9px] mt-2 italic text-slate-500">{m.hint}</div> : null}
            </div>
          ))}
        </div>
      </div>

      {/* 3) MODULE forensic timeline (inchangé) */}
      <div className="bg-black/40 rounded-[56px] border border-white/10 overflow-hidden flex flex-col">
        <header className="p-8 border-b border-white/5 bg-white/[0.02] flex justify-between items-center">
          <h5 className="text-xs font-black text-slate-400 uppercase tracking-[0.3em]">Timeline des Events (Forensic)</h5>
          <div className="px-4 py-1 rounded-full bg-white/5 text-[9px] font-black text-slate-500 uppercase italic">
            Cliquez pour isoler le bruit
          </div>
        </header>

        <div className="p-6 h-[400px] overflow-y-auto custom-scrollbar space-y-3">
          {ghostNotes.length > 0 ? (
            ghostNotes.map((g, i) => <GhostAuditItem key={i} item={g} onSeek={handleSeek} />)
          ) : (
            <div className="h-full flex flex-col items-center justify-center gap-4 opacity-20">
              <div className="text-6xl">🧼</div>
              <span className="text-[10px] font-black uppercase tracking-[0.5em]">Jeu Parfaitement Nettoyé</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
